import { ref } from 'vue';

const DEFAULT_DURATION_MS = 1500;
const THROTTLE_MS = 1000;
const PLACEMENT_GAP = 6;
const VIEWPORT_PAD = 8;
const H5_HINT_BREAKPOINT = 768;
const H5_HINT_FONT_SCALE = 2 / 3;

/** @typedef {'top' | 'right' | 'left' | 'bottom'} HintPlacement */

/** @type {HintPlacement[]} */
const PLACEMENT_ORDER = ['top', 'right', 'left', 'bottom'];

/** @type {import('vue').Ref<{ id: number, text: string, top: number, left: number, color: string, fontSize: string, fontWeight: string } | null>} */
export const activeHint = ref(null);

let hintSeq = 0;
let hideTimer = null;
let rafId = null;
/** @type {WeakMap<HTMLElement, number>} */
const lastShownAtByAnchor = new WeakMap();
/** @type {{ anchor: HTMLElement, text: string, typography: { fontSize: string, fontWeight: string }, placement: HintPlacement } | null} */
let activeSession = null;

function resolveAnchorEl(anchor) {
  if (!anchor) return null;
  if (anchor instanceof HTMLElement) return anchor;
  if (
    anchor &&
    typeof anchor === 'object' &&
    '$el' in anchor &&
    anchor.$el instanceof HTMLElement
  ) {
    return anchor.$el;
  }
  if (anchor && typeof anchor === 'object' && 'value' in anchor) {
    return resolveAnchorEl(anchor.value);
  }
  return null;
}

/** 解析操作按钮锚点：优先外层 button，且必须是仍在文档内的节点 */
export function resolveHintAnchor(anchor) {
  const el = resolveAnchorEl(anchor);
  if (!(el instanceof HTMLElement)) return null;
  if (!document.contains(el)) return null;

  const interactive =
    el.closest(
      'button, [role="button"], .referrals-page__copy-btn, .h5-referrals__copy-btn, .deposit-dlg__copy-btn, .h5-icon-button'
    ) ?? el;

  if (!(interactive instanceof HTMLElement) || !document.contains(interactive)) {
    return document.contains(el) ? el : null;
  }
  return interactive;
}

function parseRgb(color) {
  const raw = String(color ?? '').trim();
  if (!raw || raw === 'transparent') return null;
  if (raw.startsWith('#')) {
    const hex = raw.slice(1);
    if (hex.length === 3) {
      return {
        r: parseInt(hex[0] + hex[0], 16),
        g: parseInt(hex[1] + hex[1], 16),
        b: parseInt(hex[2] + hex[2], 16),
      };
    }
    if (hex.length >= 6) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }
  }
  const match = raw.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (!match) return null;
  return {
    r: Number(match[1]),
    g: Number(match[2]),
    b: Number(match[3]),
  };
}

function relativeLuminance({ r, g, b }) {
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

function isTransparentColor(color) {
  const raw = String(color ?? '')
    .trim()
    .toLowerCase();
  if (!raw || raw === 'transparent') return true;
  const match = raw.match(/rgba?\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*,\s*([\d.]+)\s*\)/i);
  if (match) return Number(match[1]) <= 0.05;
  return false;
}

/** 与操作点配色一致：实心按钮取背景色，文本/线框按钮取文字色 */
export function resolveAnchorHintColor(el) {
  if (!(el instanceof HTMLElement) || typeof window === 'undefined') {
    return 'var(--text-color, #0f172a)';
  }

  const icon = el.querySelector('.el-icon, svg');
  const source = icon instanceof HTMLElement ? icon : el;
  const style = window.getComputedStyle(source);
  const fg = style.color;
  const bg = window.getComputedStyle(el).backgroundColor;
  const fgRgb = parseRgb(fg);
  const bgRgb = parseRgb(bg);

  if (fgRgb && bgRgb && !isTransparentColor(bg)) {
    const fgLum = relativeLuminance(fgRgb);
    const bgLum = relativeLuminance(bgRgb);
    if (fgLum > 0.82 && bgLum < 0.72) return bg;
    if (fgLum < 0.25 && bgLum > 0.88) return fg;
  }

  if (fgRgb && relativeLuminance(fgRgb) > 0.82 && bgRgb && !isTransparentColor(bg)) {
    return bg;
  }

  return fg || 'var(--text-color, #0f172a)';
}

function scaleHintFontSize(fontSizePx) {
  const scaled = fontSizePx * H5_HINT_FONT_SCALE;
  return `${Math.max(8, Math.round(scaled * 10) / 10)}px`;
}

function resolveAnchorHintTypography(el) {
  if (!(el instanceof HTMLElement) || typeof window === 'undefined') {
    return { fontSize: '12px', fontWeight: '600' };
  }
  const style = window.getComputedStyle(el);
  let fontSize = style.fontSize || '12px';
  if (window.innerWidth <= H5_HINT_BREAKPOINT) {
    fontSize = scaleHintFontSize(parseFloat(fontSize) || 12);
  }
  return {
    fontSize,
    fontWeight: style.fontWeight || '600',
  };
}

function measureHintSize(text, typography) {
  const fontSize = parseFloat(typography.fontSize) || 12;
  const hasCjk = /[\u4e00-\u9fff\u3400-\u4dbf]/.test(text);
  const charWidth = fontSize * (hasCjk ? 0.95 : 0.55);
  const viewportW = typeof window !== 'undefined' ? window.innerWidth : 360;
  const width = Math.min(Math.ceil(text.length * charWidth + 4), viewportW - VIEWPORT_PAD * 2);
  const height = Math.ceil(fontSize * 1.35);
  return { width: Math.max(width, 24), height };
}

/** hint 比按钮宽时右对齐按钮，避免盖住左侧 input */
function resolveVerticalHintLeft(anchorRect, hintSize) {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 360;
  let left =
    hintSize.width <= anchorRect.width
      ? anchorRect.left + (anchorRect.width - hintSize.width) / 2
      : anchorRect.right - hintSize.width;

  const maxLeft = vw - VIEWPORT_PAD - hintSize.width;
  left = Math.max(VIEWPORT_PAD, Math.min(left, maxLeft));
  return left;
}

/**
 * @param {HintPlacement} placement
 * @param {DOMRect} anchorRect
 * @param {{ width: number, height: number }} hintSize
 */
function getHintBox(placement, anchorRect, hintSize) {
  const gap = PLACEMENT_GAP;
  const cy = anchorRect.top + anchorRect.height / 2;

  switch (placement) {
    case 'right':
      return {
        left: anchorRect.right + gap,
        top: cy - hintSize.height / 2,
        width: hintSize.width,
        height: hintSize.height,
      };
    case 'left':
      return {
        left: anchorRect.left - gap - hintSize.width,
        top: cy - hintSize.height / 2,
        width: hintSize.width,
        height: hintSize.height,
      };
    case 'bottom':
      return {
        left: resolveVerticalHintLeft(anchorRect, hintSize),
        top: anchorRect.bottom + gap,
        width: hintSize.width,
        height: hintSize.height,
      };
    case 'top':
    default:
      return {
        left: resolveVerticalHintLeft(anchorRect, hintSize),
        top: anchorRect.top - gap - hintSize.height,
        width: hintSize.width,
        height: hintSize.height,
      };
  }
}

function isOutOfViewport(box) {
  const vw = window.innerWidth || 0;
  const vh = window.innerHeight || 0;
  return (
    box.left < VIEWPORT_PAD ||
    box.top < VIEWPORT_PAD ||
    box.left + box.width > vw - VIEWPORT_PAD ||
    box.top + box.height > vh - VIEWPORT_PAD
  );
}

function hasDirectText(el) {
  for (const node of el.childNodes) {
    if (node.nodeType === Node.TEXT_NODE && String(node.textContent ?? '').trim()) {
      return true;
    }
  }
  return false;
}

/** 是否属于按钮所在输入行（左侧兄弟 input 文本，不应挡「上方」） */
function isInputRowSibling(anchorEl, el) {
  const row = anchorEl.closest(
    '.referrals-page__input-row, .h5-referrals__readonly-row, .deposit-dlg__copy-row, [data-anchor-hint-row]'
  );
  return Boolean(row && row.contains(el) && el !== anchorEl && !anchorEl.contains(el));
}

/**
 * 判断元素是否为 hint 需要避让的内容（相对当前操作按钮）。
 * @param {Element} el
 * @param {HTMLElement} anchorEl
 * @param {HintPlacement} placement
 */
function isObstacle(el, anchorEl, placement) {
  if (!(el instanceof Element)) return false;
  if (el === document.body || el === document.documentElement || el.id === 'app') return false;
  if (el.closest('.ds-anchor-hint')) return false;
  if (el === anchorEl || anchorEl.contains(el) || el.contains(anchorEl)) return false;

  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden' || style.pointerEvents === 'none') {
    return false;
  }
  const opacity = parseFloat(style.opacity);
  if (!Number.isNaN(opacity) && opacity < 0.08) return false;

  // 表单 label 不算遮挡
  if (el.matches('label')) return false;

  // 上下方向：同一输入行里的 input/文本在按钮左侧，不应挡「上方/下方」
  if ((placement === 'top' || placement === 'bottom') && isInputRowSibling(anchorEl, el)) {
    return false;
  }

  if (
    el.matches(
      'img, video, canvas, picture, input, textarea, select, button, a, svg, [role="img"], .el-input__inner, .el-textarea__inner'
    )
  ) {
    return true;
  }

  if (el.matches('h1, h2, h3, h4, h5, h6, p, li, td, th')) {
    return Boolean(el.textContent?.trim());
  }

  if (el.matches('span')) {
    return Boolean(el.textContent?.trim());
  }

  return hasDirectText(el);
}

/** @param {{ left: number, top: number, width: number, height: number }} box @param {HTMLElement} anchorEl @param {HintPlacement} placement */
function isHintBoxBlocked(box, anchorEl, placement) {
  const sampleRatios = [
    [0.5, 0.5],
    [0.15, 0.2],
    [0.85, 0.2],
    [0.15, 0.8],
    [0.85, 0.8],
  ];

  for (const [rx, ry] of sampleRatios) {
    const x = box.left + box.width * rx;
    const y = box.top + box.height * ry;
    if (x < 0 || y < 0 || x > window.innerWidth || y > window.innerHeight) continue;

    const stack = document.elementsFromPoint(x, y);
    for (const el of stack) {
      if (!(el instanceof Element)) continue;
      if (el === document.body || el === document.documentElement) break;
      if (el.id === 'app') continue;
      if (isObstacle(el, anchorEl, placement)) return true;
      if (isTransparentColor(window.getComputedStyle(el).backgroundColor)) continue;
      break;
    }
  }

  return false;
}

/**
 * 按 上 → 右 → 左 → 下 选取首个可用方向；坐标即 hint 左上角（与渲染一致）。
 * @param {HTMLElement} el
 * @param {string} text
 * @param {{ fontSize: string, fontWeight: string }} typography
 * @param {HintPlacement} [lockedPlacement]
 */
function pickHintBox(el, text, typography, lockedPlacement) {
  const anchorRect = el.getBoundingClientRect();
  const hintSize = measureHintSize(text, typography);

  if (lockedPlacement) {
    return { box: getHintBox(lockedPlacement, anchorRect, hintSize), placement: lockedPlacement };
  }

  for (const placement of PLACEMENT_ORDER) {
    const box = getHintBox(placement, anchorRect, hintSize);
    if (isOutOfViewport(box)) continue;
    if (isHintBoxBlocked(box, el, placement)) continue;
    return { box, placement };
  }

  return { box: getHintBox('top', anchorRect, hintSize), placement: 'top' };
}

function updateActiveHintPosition() {
  if (!activeSession || !activeHint.value) return;

  const { anchor, text, typography, placement } = activeSession;
  if (!document.contains(anchor)) {
    hideActiveHint();
    return;
  }

  const { box } = pickHintBox(anchor, text, typography, placement);
  activeHint.value = {
    ...activeHint.value,
    top: box.top,
    left: box.left,
  };
}

function schedulePositionUpdate() {
  if (typeof window === 'undefined' || !activeSession) return;
  if (rafId != null) return;
  rafId = window.requestAnimationFrame(() => {
    rafId = null;
    updateActiveHintPosition();
  });
}

function attachScrollListeners() {
  if (typeof window === 'undefined') return;
  window.addEventListener('scroll', schedulePositionUpdate, true);
  window.addEventListener('resize', schedulePositionUpdate);
}

function detachScrollListeners() {
  if (typeof window === 'undefined') return;
  window.removeEventListener('scroll', schedulePositionUpdate, true);
  window.removeEventListener('resize', schedulePositionUpdate);
  if (rafId != null) {
    window.cancelAnimationFrame(rafId);
    rafId = null;
  }
}

function clearHintTimer() {
  if (hideTimer != null) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
}

function hideActiveHint() {
  clearHintTimer();
  detachScrollListeners();
  activeSession = null;
  activeHint.value = null;
}

/**
 * @param {object} options
 * @param {HTMLElement | import('vue').Ref | Event['currentTarget']} options.anchor
 * @param {string} options.text
 * @param {number} [options.duration]
 * @param {boolean} [options.skipDedupe]
 */
export function showAnchorHint({
  anchor,
  text,
  duration = DEFAULT_DURATION_MS,
  skipDedupe = false,
}) {
  const msg = String(text ?? '').trim();
  if (!msg) return;

  const el = resolveHintAnchor(anchor);
  if (!el) return;

  const now = Date.now();
  if (!skipDedupe) {
    const last = lastShownAtByAnchor.get(el) ?? 0;
    if (now - last < THROTTLE_MS) return;
    lastShownAtByAnchor.set(el, now);
  }

  detachScrollListeners();
  clearHintTimer();
  const typography = resolveAnchorHintTypography(el);
  const { box, placement } = pickHintBox(el, msg, typography);

  activeSession = { anchor: el, text: msg, typography, placement };
  attachScrollListeners();

  activeHint.value = {
    id: ++hintSeq,
    text: msg,
    top: box.top,
    left: box.left,
    color: resolveAnchorHintColor(el),
    fontSize: typography.fontSize,
    fontWeight: typography.fontWeight,
  };

  hideTimer = setTimeout(() => {
    hideTimer = null;
    hideActiveHint();
  }, Math.max(duration, 400));
}

export function useAnchorHintState() {
  return { activeHint };
}

export function useAnchorHint() {
  return { show: showAnchorHint };
}

/** @deprecated 保留导出避免旧引用报错；现用左上角直定位，不再依赖 transform */
export function anchorHintTransform() {
  return 'none';
}
